export const version = "v1.0.30";
export const defaultSearchType = 'ALL';

// Language color configuration - maps first 2 characters of language code to Tailwind color classes
export const languageColors = {
    // Primary languages with distinct colors
    'en': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },      // English - Blue
    'fr': { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' }, // French - Purple
    'es': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' }, // Spanish - Orange
    'nl': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },    // Dutch - Green

    // Secondary languages with less critical color differences
    'de': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' }, // German - Yellow
    'it': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },          // Italian - Red
    'pt': { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-300' },       // Portuguese - Pink
    'zh': { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300' }, // Chinese - Indigo
    'ja': { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-300' },       // Japanese - Cyan
    'ko': { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-300' },       // Korean - Teal
    'ar': { bg: 'bg-lime-100', text: 'text-lime-800', border: 'border-lime-300' },       // Arabic - Lime
    'ru': { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-300' },       // Russian - Rose
    'sv': { bg: 'bg-sky-100', text: 'text-sky-800', border: 'border-sky-300' },          // Swedish - Sky
    'da': { bg: 'bg-violet-100', text: 'text-violet-800', border: 'border-violet-300' }, // Danish - Violet
    'no': { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800', border: 'border-fuchsia-300' }, // Norwegian - Fuchsia
    'fi': { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' }, // Finnish - Emerald

    // Default fallback for unknown languages
    'default': { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' }
};

/**
 * Get color classes for a language code based on its first 2 characters
 * @param {string} langCode - The language code (e.g., 'en', 'fr-CA', 'es')
 * @returns {object} Object with bg, text, and border color classes
 */
export const getLanguageColors = (langCode) => {
    if (!langCode) return languageColors.default;

    const langPrefix = langCode.toLowerCase().substring(0, 2);
    return languageColors[langPrefix] || languageColors.default;
};

export const releaseNotes = [
        {
        version: "v1.0.30",
        date: "2026-06-10",
        features: [
            "Improvements to Children, ie date formatting, team data, episode title",
        ],
    },  
    {
        version: "v1.0.29",
        date: "2026-06-08",
        features: [
            "Explain why series episode counts do not always match the number of episodes in the episode list (eg due to missing upstream data)",
        ],
    },  
    {
        version: "v1.0.28",
        date: "2026-05-26",
        features: [
            "Added support for Podcast titles and episodes in search results",
        ],
    },      
    {
        version: "v1.0.27",
        date: "2026-05-22",
        features: [
            "Added support for episode titles in search results",
            "Updated runtime display to use localized format"
        ],
    },    
    {
        version: "v1.0.26",
        date: "2026-04-27",
        features: [
            "Season details are now shown in the program overview page when relevant",
        ],
    },
    {
        version: "v1.0.25",
        date: "2026-03-10",
        features: [
            "Allow back button to work properly after comparing multiple programs",
            "Fix cast and crew for actors with one name (eg 'Madonna')",
        ],
    },
    {
        version: "v1.0.24",
        date: "2026-02-28",
        features: [
            "Show episode title language when available in program details page",
        ],
    },
    {
        version: "v1.0.23",
        date: "2026-02-21",
        features: [
            "Security updates: Use rolling windows for jwt token lifetime.  48 Hr default session timer.",
            "Starts-with searching is enabled by default"
        ],
    },
    {
        version: "v1.0.22",
        date: "2026-02-11",
        features: [
            "Security updates: jti claim is now included in JWT tokens to prevent replay attacks and enhance security.",
        ],
    },
    {
        version: "v1.0.21",
        date: "2026-02-11",
        features: [
            "Update string normalization to better handle special characters and accents in program titles",
            "Improved search bar with new Exact and Starts With search options for more precise searching",
            "Admin backend fix allowing local administrators to manage user permissions without needing super admin access",
            "Add MCP support for performing certain queries within an AI (work in progress)",
            "Security updates",
	        "Performance optimizations"
        ],
    },
    {
        version: "v1.0.20",
        date: "2026-01-06",
        features: [
            "Update language tags with different colours for easier identification",
            "tbd"
        ],
    },
    {
        version: "v1.0.19",
        date: "2025-12-28",
        features: [
            "Do not use expired images in quick search / program overview results",
            "Fix screen reload behaviour on Episodes page"
        ],
    },
 {
        version: "v1.0.18",
        date: "2025-12-09",
        features: [
            "Internationalization fixes",
            "Addition of dutch language translations"
        ],
    },
   {
        version: "v1.0.17",
        date: "2025-12-02",
        features: [
            "Timezones can be set in settings (UTC) and will apply to airings and lineups",
            "Lineup schedule times are now shown in user's selected timezone",
            "Series episodes are now maintained when navigating between program details pages",
        ], 
    },
    {
        version: "v1.0.16",
        date: "2025-11-27",
        features: [
            "Ensure original air dates do not get converted between timezones",
            "Allow program comparisons on the Saved Items page",
        ], 
    },
    {
        version: "v1.0.15",
        date: "2025-11-25",
        features: [
            "Show season details in overview page when relevant",
            "Switch to using iconic 16:9 image for program details page",
        ], 
    },
    {
        version: "v1.0.14",
        date: "2025-11-20",
        features: [
            "Internationalization updates"
        ],
    },
    {
        version: "v1.0.12",
        date: "2025-11-17",
        features: [
            "Episode list now includes program descriptions (size=40)",
            "Sports Event Children programs can now be downloaded as CSV",
            "Channel lineup details for a linup can now be downloaded as CSV",
        ],
    },
    {
        version: "v1.0.10",
        date: "2025-11-12",
        features: [
            "Bugfix with bookmarks all showing as set on main page",
            "Performance metrics can be turned off in settings",
            "Sports organization fix.  Database issue with carrier determination"
        ],
    },
    {
        version: "v1.0.9",
        date: "2025-11-04",
        features: [
            "Bugfixes on Episodes page including now adding proper sorting of episodes by season and episode number.",
            "Experiment with auto-focus on input box on main screen and celebrities search page.",
        ],
    },
    {
        version: "v1.0.8",
        date: "2025-10-29",
        features: [
            "Introduced backends and permissions management pages in the admin section.",
        ],
    },
    {
        version: "v1.0.7",
        date: "2025-10-22",
        features: [
            "Switched to Google OAuth for Production.",
        ],
    },
    {
        version: "v1.0.6",
        date: "2025-10-18",
        features: [
            "Further database optimizations to improve search performance.",
            "Images: Add new 'Season Images' tab in program details to show season-specific images.",
        ],
    },
    {
        version: "v1.0.5",
        date: "2025-07-18",
        features: [
            "Search Bar: provide quick search options for Series and Feature Films.",
            "Images: Allow download of selected images or download of all program images.",
            "Images: Clearly identify images marked as expired by Gracenote.",
            "Sports Events: Better searching for sports teams.",
            "Sports Events: Filter by Game Date and Season.",
            "Lineups: Fixed issue that limited to 100 days of schedules.",
            "Lineups: Show episode title in lineup schedule which includes team names if a sporting event.",
            "Program Details: Allow right-click to open a series episode in a new tab.",
        ],
    },
    {
        version: "v1.0.4",
        date: "2025-07-08",
        features: [
            "Program Details Page: Enable download buttons for all components (ie Releases, Availabilities, Images, etc)",
        ],
    },
    {
        version: "v1.0.3",
        date: "2025-07-07",
        features: [
            "Main Search: Show both Title and Description languages in program details page.",
        ],
    },
    {
        version: "v1.0.2",
        date: "2025-06-27",
        features: [
            "Main Search: Implement user-profile based search results on language preference.",
        ],
    },
    {
        version: "v1.0.1",
        date: "2025-06-25",
        features: [
            "Main Search: populate release year with movie release year.",
            "Main Search: populate description language.",
            "",
            "We are still studying the impact on performance of these changes.",
            "You cannot currently refine your search based on the description language."
        ],
    },
];
